/**
 * KBRouteGuard — route guard for authenticated KB pages.
 *
 * Wraps authenticated KB article routes (/help/kb/*).
 * If the user is not authenticated, redirects to /login?returnUrl=<current path>.
 * If the user lacks the required role (403), the wrapped component handles the display.
 *
 * Usage:
 *   <KBRouteGuard>
 *     <AuthKBArticlePage />
 *   </KBRouteGuard>
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';

interface KBRouteGuardProps {
  children: ReactNode;
}

export function KBRouteGuard({ children }: KBRouteGuardProps) {
  const { isAuthenticated, isLoading } = useUser();
  const location = useLocation();

  // While checking auth state, render nothing (or a spinner)
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse space-y-3">
          <div className="h-3 bg-neutral-200 rounded w-40" />
          <div className="h-3 bg-neutral-100 rounded w-32" />
        </div>
      </div>
    );
  }

  // Not authenticated — redirect to login with return URL
  if (!isAuthenticated) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  return <>{children}</>;
}

export default KBRouteGuard;
